import { useState } from 'react'
import { X, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/v2/ui'
import type { CompetitorResult } from '@/lib/api/amazon-listing'

interface CompetitorListProps {
  items: CompetitorResult[]
  onChange: (items: CompetitorResult[]) => void
}

export function CompetitorList({ items, onChange }: CompetitorListProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')

  const remove = (asin: string) => onChange(items.filter(i => i.asin !== asin))

  const add = () => {
    const v = inputValue.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (!v || items.some(i => i.asin === v)) return
    onChange([...items, { asin: v, title: t('campaignCreator.competitors.manualEntry'), price: null, rating: null, reviews_count: null }])
    setInputValue('')
  }

  return (
    <div className="campaign-creator-competitor-list flex flex-col gap-2">
      <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {items.map((item, i) => (
          <div key={item.asin}
               className="competitor-item flex items-center gap-2.5 px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 text-sm">
            <span className="text-xs text-neutral-400 font-mono w-4 shrink-0">{i + 1}</span>
            <span className="font-mono text-xs font-medium text-primary-500 w-24 shrink-0">{item.asin}</span>
            <span className="flex-1 text-neutral-600 dark:text-neutral-300 truncate">{item.title}</span>
            <Badge variant="secondary" size="sm">{t('campaignCreator.competitors.aiBadge')}</Badge>
            <button onClick={() => remove(item.asin)} aria-label={t('campaignCreator.competitors.remove')}
                    className="h-5 w-5 rounded flex items-center justify-center text-neutral-400 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-500 transition-colors">
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder={t('campaignCreator.competitors.addPlaceholder')}
          className="flex-1 h-9 rounded-[9px] border border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800 px-3 font-mono text-xs text-neutral-800 dark:text-white outline-none focus:border-primary-500 focus:border-solid"
          aria-label={t('campaignCreator.competitors.addLabel')}
        />
        <button onClick={add}
                className="h-9 px-3 rounded-[9px] border border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800 text-xs font-semibold text-neutral-600 dark:text-neutral-300 hover:border-primary-500 hover:text-primary-500 hover:bg-primary-500/10 transition-colors flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t('campaignCreator.competitors.addBtn')}
        </button>
      </div>
    </div>
  )
}
